import React, {useEffect, useRef, useState} from 'react'
import {Card, Row, Col, Form} from 'react-bootstrap'
import Chart from 'react-apexcharts'
import ReactApexChart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {
  getTotalUsage,
  getTransactionStatistic,
  selectTotalUsage,
  selectTransactionStatistics,
} from '../redux/BillingRedux'
import {convertTransactionStatisticToChartData} from '../utils'
import {DATE_FORMAT} from '../../../shared/constants'
import moment from 'moment'
import {useDispatch} from 'react-redux'
import {useIntl} from 'react-intl'

enum DATE_FILTER_RANGE {
  LAST_7_DAYS = 'last_7_days',
  THIS_MONTH = 'this_month',
  LAST_MONTH = 'last_month',
  CUSTOM = 'custom',
}

const BillingStatistics: React.FC = () => {
  const didRequest = useRef(false)

  const intl = useIntl()
  // State for bar chart filter
  const dispatch = useDispatch()
  const [barFilter, setBarFilter] = useState<DATE_FILTER_RANGE>(DATE_FILTER_RANGE.LAST_7_DAYS)
  const [customStart, setCustomStart] = useState<string>(
    moment().startOf('month').format(DATE_FORMAT)
  )
  const [customEnd, setCustomEnd] = useState<string>(moment().format(DATE_FORMAT))
  const [customSearchClicked, setCustomSearchClicked] = useState(false)

  const transactionStatistics = useAppSelector(selectTransactionStatistics)
  const chartData = convertTransactionStatisticToChartData(transactionStatistics)
  const totalCreditUsage = useAppSelector(selectTotalUsage)
  const barChartData = {
    series: chartData.series,
    options: {
      chart: {
        height: 250,
        type: 'area',
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
      },
      xaxis: {
        type: 'date',
        categories: chartData.labels,
      },
      // tooltip: {
      //   x: {
      //     format: DATE_ONLY_FORMAT,
      //   },
      // },
    },
  }
  const usePercentage = ((totalCreditUsage.totalOut / totalCreditUsage.totalIn) * 100 || 0).toFixed(
    2
  )
  const radialBarChartState = {
    series: [Number(usePercentage)],
    options: {
      chart: {
        type: 'radialBar',
        offsetY: -20,
        sparkline: {
          enabled: true,
        },
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        style: {
          fontSize: '12px',
          fontFamily: undefined,
        },
        x: {
          show: true,
          format: 'dd/MM/yyyy',
          formatter: undefined,
        },
        // y: {
        //   formatter: undefined,
        //   format: {
        //     formatter: (seriesName: any) => seriesName + 'huy',
            
        //   },
        // },
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          track: {
            background: '#e7e7e7',
            strokeWidth: '97%',
            margin: 5, // margin is in pixels
            dropShadow: {
              enabled: true,
              top: 2,
              left: 0,
              color: '#444',
              opacity: 1,
              blur: 2,
            },
          },
          dataLabels: {
            name: {
              show: false,
            },
            value: {
              offsetY: -2,
              fontSize: '22px',
            },
          },
        },
      },
      grid: {
        padding: {
          top: -10,
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          shadeIntensity: 0.4,
          inverseColors: false,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 50, 53, 91],
        },
      },
      labels: [intl.formatMessage({id: 'Billing.credit_usage'})],
    },
  }
  useEffect(() => {
    let endDate = moment()
    let startDate = moment()
    if (barFilter === DATE_FILTER_RANGE.CUSTOM) {
      if (!customSearchClicked) return
      startDate = moment(customStart)
      endDate = moment(customEnd)
    } else {
      setCustomSearchClicked(false)
      switch (barFilter) {
        case DATE_FILTER_RANGE.LAST_7_DAYS:
          startDate.subtract(7, 'days')
          break
        case DATE_FILTER_RANGE.THIS_MONTH:
          startDate = moment(startDate).startOf('month')
          break
        case DATE_FILTER_RANGE.LAST_MONTH:
          startDate = moment(startDate).subtract(1, 'month').startOf('month')
          endDate = moment(startDate).endOf('month')
          break
        default:
          startDate = moment(startDate).startOf('month')
          endDate = moment(startDate).endOf('month')
      }
    }

    dispatch(
      getTransactionStatistic({startDate: startDate.toISOString(), endDate: endDate.toISOString()})
    )
  }, [barFilter, customSearchClicked])
  useEffect(() => {
    if (didRequest.current) return
    didRequest.current = true
    // Initial fetch for the bar chart data
    dispatch(getTotalUsage())
  }, [])
  return (
    <Card className='mb-10'>
      <Card.Body>
        <Row className='d-flex justify-content-between align-items-center mb-3'>
          <Col className='d-flex align-items-center' xs='auto'>
            <Form.Select
              style={{width: 180}}
              value={barFilter}
              onChange={(e) => {
                setBarFilter(e.target.value as DATE_FILTER_RANGE)
                setCustomSearchClicked(false)
              }}
            >
              <option value={DATE_FILTER_RANGE.LAST_7_DAYS}>{intl.formatMessage({id: 'order.filter.last_7_days'})}</option>
              <option value={DATE_FILTER_RANGE.THIS_MONTH}>{intl.formatMessage({id: 'order.filter.this_month'})}</option>
              <option value={DATE_FILTER_RANGE.LAST_MONTH}>{intl.formatMessage({id: 'order.filter.last_month'})}</option>
              <option value={DATE_FILTER_RANGE.CUSTOM}>{intl.formatMessage({id: 'BILLING.STATISTICS.CUSTOM'})}</option>
            </Form.Select>
            {barFilter === DATE_FILTER_RANGE.CUSTOM && (
              <div className='d-flex align-items-center ms-3'>
                <span className='mx-1'>{intl.formatMessage({id: 'BILLING.STATISTICS.FROM'})}</span>
                <Form.Control
                  type='date'
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  style={{width: 140, marginRight: 8}}
                />
                <span className='mx-1'>{intl.formatMessage({id: 'BILLING.STATISTICS.TO'})}</span>
                <Form.Control
                  type='date'
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  style={{width: 140, marginLeft: 8}}
                />
                <button
                  className='btn btn-primary ms-3'
                  onClick={() => setCustomSearchClicked((prev) => !prev)}
                  disabled={
                    !customStart || 
                    !customEnd || 
                    moment(customStart).isSameOrAfter(moment(customEnd))
                  }
                >
                  {intl.formatMessage({id: 'BILLING.STATISTICS.SEARCH'})}
                </button>
              </div>
            )}
          </Col>
          <Col xs='auto' className='ms-auto'>
            {barFilter === DATE_FILTER_RANGE.LAST_MONTH && (
              <button
                style={{width: 220}}
                className='btn btn-primary'
                onClick={() => {
                  // TODO: Implement download invoice logic
                  alert(intl.formatMessage({id: 'BILLING.STATISTICS.DOWNLOAD_COMING_SOON'}))
                }}
              >
                <i className='bi bi-download me-2'></i>{intl.formatMessage({id: 'BILLING.STATISTICS.DOWNLOAD_INVOICE'})}
              </button>
            )}
          </Col>
        </Row>

        <Row>
          <Col md={8}>
            <h5>{intl.formatMessage({id: 'BILLING.STATISTICS.ORDERS_BY_DAY'})}</h5>
            {chartData.labels.length > 0 && (
              <ReactApexChart
                options={barChartData.options as ApexOptions}
                series={barChartData.series}
                type='area'
                height={250}
              />
            )}
          </Col>

          <Col md={4}>
            <h5>{intl.formatMessage({id: 'BILLING.STATISTICS.CURRENT_ACCOUNT_CREDIT'})}</h5>
            <Chart
              options={radialBarChartState.options as ApexOptions}
              series={radialBarChartState.series}
              type='radialBar'
              height={400}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}

export default BillingStatistics
